/**
 * The colour × size grid (SCOPE §5.2).
 *
 * "Enter a style once, define colors (rows) × sizes (columns), type
 * quantities/costs in the matrix — variants generate automatically. This
 * mirrors how buyers think and is the client's explicitly requested format."
 *
 * Pure, and tested directly. What makes this fast is not the grid drawing: it
 * is that the axes are *typed as lists* — `Black, Red, Storm Blue` and
 * `S, M, L, XL` — and the cross product is assumed to exist. A buyer who names
 * four colours and four sizes wants sixteen variants; the rare gap (no XS in
 * that colourway) is unticked, not built up one cell at a time (§2.2-5).
 */

export interface MatrixCell {
  /** Stable key: what identifies this colour/size pair across re-renders. */
  readonly key: string;
  readonly color: string | null;
  readonly size: string | null;
  /**
   * The third axis — Short, Regular, Long — where the style has one
   * (2026-10-02). Stored as the size's alt size; null for most styles.
   */
  readonly length: string | null;
  /** Already on this style. Shown, never re-created (§2.2-13). */
  readonly exists: boolean;
  readonly selected: boolean;
}

export interface MatrixInput {
  readonly colors: readonly string[];
  readonly sizes: readonly string[];
  /** Lengths, when the style comes in them; empty or absent when it does not. */
  readonly lengths?: readonly string[];
  /**
   * Cells the buyer has pressed away from how they start: off for a cell that
   * starts on, on for one that starts off.
   */
  readonly deselected: ReadonlySet<string>;
  /** Cells this style already has. */
  readonly existing: ReadonlySet<string>;
  /** Cells that start unticked — see `gapsStartOff`. Absent: every new cell starts ticked. */
  readonly startsOff?: (cell: {
    color: string | null;
    size: string | null;
    length: string | null;
  }) => boolean;
}

/**
 * On a style that already exists, a combination of things it already comes in
 * — a color, a size and a length each already on it — that it does not have is
 * a gap the shop chose, not something to fill. It starts unticked; a cell with
 * anything newly typed starts ticked (2026-10-15: a style with lengths offered
 * its 259 gaps ticked, one press from being made). Making a style, nothing
 * exists and every cell starts ticked, as it always has.
 */
export function gapsStartOff(
  existing: readonly {
    colorName: string | null;
    sizeLabel: string | null;
    altSize: string | null;
  }[],
): (cell: { color: string | null; size: string | null; length: string | null }) => boolean {
  const fold = (value: string | null) => (value ?? '').toUpperCase();
  const colors = new Set(existing.map((variant) => fold(variant.colorName)));
  const sizes = new Set(existing.map((variant) => fold(variant.sizeLabel)));
  const lengths = new Set(existing.map((variant) => fold(variant.altSize)));
  return (cell) =>
    existing.length > 0 &&
    colors.has(fold(cell.color)) &&
    sizes.has(fold(cell.size)) &&
    lengths.has(fold(cell.length));
}

/**
 * Split a typed list into axis values.
 *
 * Commas, newlines and tabs all separate, because staff paste from a vendor
 * email as often as they type. Duplicates are dropped case-insensitively but
 * the first spelling wins — if they wrote `Black` then `black`, the label they
 * chose first is the one that reaches the label printer.
 */
export function parseAxis(raw: string): string[] {
  const seen = new Set<string>();
  const values: string[] = [];

  for (const part of raw.split(/[,\n\t]/)) {
    const value = part.trim();
    if (value === '') continue;

    const fold = value.toUpperCase();
    if (seen.has(fold)) continue;
    seen.add(fold);
    values.push(value);
  }

  return values;
}

/**
 * Identity of a colour/size/length cell. Case-folded, so `Black`/`black` are
 * one cell. A size with no length is a different cell from the same size in
 * Long — a pant's 32 Regular and 32 Long are two things on the shelf.
 */
export function cellKey(
  color: string | null,
  size: string | null,
  length: string | null = null,
): string {
  return `${(color ?? '').toUpperCase()}|${(size ?? '').toUpperCase()}|${(length ?? '').toUpperCase()}`;
}

/**
 * Every cell the grid should draw.
 *
 * An empty axis is not an empty grid — it means that axis does not apply. A tub
 * of wax has neither colour nor size and is exactly one variant; a helmet has
 * both. Collapsing to `null` rather than to an empty string matters because
 * `null` is what the column stores, and what the SKU builder skips.
 */
export function matrixCells(input: MatrixInput): MatrixCell[] {
  const colors: (string | null)[] = input.colors.length === 0 ? [null] : [...input.colors];
  const sizes: (string | null)[] = input.sizes.length === 0 ? [null] : [...input.sizes];
  const lengths: (string | null)[] =
    (input.lengths ?? []).length === 0 ? [null] : [...input.lengths!];

  const cells: MatrixCell[] = [];
  // Length outermost: the dialog draws one grid per length, as every grid does.
  for (const length of lengths) {
    for (const color of colors) {
      for (const size of sizes) {
        const key = cellKey(color, size, length);
        const exists = input.existing.has(key);
        cells.push({
          key,
          color,
          size,
          length,
          exists,
          // On unless it exists, starts off, or was pressed. The common case
          // is "yes, all of these" — except the gaps in a style that exists.
          selected:
            !exists &&
            (input.startsOff?.({ color, size, length }) === true
              ? input.deselected.has(key)
              : !input.deselected.has(key)),
        });
      }
    }
  }

  return cells;
}

/** How many variants pressing Create would actually make. */
export function selectedCount(cells: readonly MatrixCell[]): number {
  return cells.filter((cell) => cell.selected).length;
}

export interface VariantDefaults {
  readonly costCents: number;
  readonly msrpCents: number | null;
  readonly retailCents: number;
  readonly targetQty: number | null;
  readonly minQty: number | null;
}

export interface VariantDraft {
  readonly colorName: string | null;
  readonly colorCode: null;
  readonly sizeLabel: string | null;
  /** The length, where the style has them. */
  readonly altSize: string | null;
  readonly vendorPartNumber: null;
  readonly barcode: null;
  readonly costCents: number;
  readonly msrpCents: number | null;
  readonly retailCents: number;
  readonly targetQty: number | null;
  readonly minQty: number | null;
}

/**
 * Turn the selected cells into variant drafts.
 *
 * Cost and price come from one set of defaults rather than per cell. Vendor
 * data does vary cost by size (§3), but a buyer entering a style by hand knows
 * one number — and the size that costs more is corrected on the detail screen,
 * where the whole style is visible at once, in one edit rather than sixteen.
 */
export function draftsFrom(
  cells: readonly MatrixCell[],
  defaults: VariantDefaults,
): VariantDraft[] {
  return cells
    .filter((cell) => cell.selected)
    .map((cell) => ({
      colorName: cell.color,
      colorCode: null,
      sizeLabel: cell.size,
      altSize: cell.length,
      vendorPartNumber: null,
      barcode: null,
      costCents: defaults.costCents,
      msrpCents: defaults.msrpCents,
      retailCents: defaults.retailCents,
      targetQty: defaults.targetQty,
      minQty: defaults.minQty,
    }));
}

/**
 * Reprice a set of variants as a percentage of MSRP (SCOPE §5.1-5).
 *
 * A variant with no MSRP is left alone rather than repriced to zero — a
 * shop-made bundle has no manufacturer's price, and silently zeroing it would
 * put it on the shelf free.
 */
export function repriceFromMsrp(
  variants: readonly { id: string; msrpCents: number | null; retailCents: number }[],
  percentOfMsrp: number,
): { variantId: string; retailCents: number }[] {
  return variants.flatMap((variant) =>
    variant.msrpCents === null || variant.msrpCents === 0
      ? []
      : [
          {
            variantId: variant.id,
            retailCents: Math.round((variant.msrpCents * percentOfMsrp) / 100),
          },
        ],
  );
}

/**
 * Margin on a variant, in percent of the retail price, or null when either
 * figure is missing.
 *
 * Shown beside the price so a buyer typing a retail price sees what it earns
 * as they type — the number they are actually deciding (§2.2-10).
 */
export function marginPercent(costCents: number, retailCents: number): number | null {
  if (retailCents <= 0) return null;
  return ((retailCents - costCents) / retailCents) * 100;
}
